(() => {
  if (window.__aeAutumnLoaded) return;
  window.__aeAutumnLoaded = true;
  document.documentElement.classList.add('ae-js');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  const reveal = (root = document) => {
    const targets = [...root.querySelectorAll('[data-ae-reveal]:not([data-ae-reveal-ready])')];
    targets.forEach((target) => target.setAttribute('data-ae-reveal-ready', ''));
    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      targets.forEach((target) => target.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.15) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -48px' });
    targets.forEach((target) => observer.observe(target));
  };

  class AeHero extends HTMLElement {
    connectedCallback() {
      if (this.aeReady) return;
      this.aeReady = true;
      this.bottles = [...this.querySelectorAll('.ae-hero__bottle-media')];
      this.motion = !reduceMotion.matches;
      this.pointerFrame = 0;
      this.pointerPosition = null;
      this.ticker = this.querySelector('.ae-hero__ticker');
      this.tickerTrack = this.querySelector('.ae-hero__ticker-track');
      this.tickerGroup = this.querySelector('.ae-hero__ticker-group');
      this.tickerItems = [...this.querySelectorAll('.ae-hero__ticker-item')];
      this.tickerDwell = Number(this.dataset.tickerDwell) || 3500;
      this.tickerTravel = Number(this.dataset.tickerTravel) || 1400;
      this.tickerOffset = 0;
      this.tickerDwellRemaining = this.tickerDwell;
      this.tickerInView = true;
      this.tickerHoverHold = false;
      this.tickerFocusHold = false;
      this.tickerPhase = 'idle';
      this.onMove = (event) => {
        if (!this.motion || !finePointer.matches) return;
        this.pointerPosition = { x: event.clientX, y: event.clientY };
        if (this.pointerFrame) return;
        this.pointerFrame = requestAnimationFrame(() => {
          this.pointerFrame = 0;
          const rect = this.getBoundingClientRect();
          const x = ((this.pointerPosition.x - rect.left) / rect.width - 0.5) * 2;
          const y = ((this.pointerPosition.y - rect.top) / rect.height - 0.5) * 2;
          this.bottles.forEach((bottle, index) => {
            const depth = (index + 1) * 3;
            bottle.style.transform = `translate3d(${x * depth}px, ${y * depth}px, 0)`;
          });
        });
      };
      this.onLeave = () => {
        this.pointerPosition = null;
        if (this.pointerFrame) cancelAnimationFrame(this.pointerFrame);
        this.pointerFrame = 0;
        this.bottles.forEach((bottle) => { bottle.style.transform = ''; });
      };
      this.addEventListener('pointermove', this.onMove, { passive: true });
      this.addEventListener('pointerleave', this.onLeave);
      this.onTickerEnter = () => {
        if (!finePointer.matches) return;
        this.tickerHoverHold = true;
        this.holdTicker();
      };
      this.onTickerLeave = () => {
        this.tickerHoverHold = false;
        this.releaseTicker();
      };
      this.onTickerFocusIn = () => {
        this.tickerFocusHold = true;
        this.holdTicker();
      };
      this.onTickerFocusOut = (event) => {
        if (event.relatedTarget && this.ticker?.contains(event.relatedTarget)) return;
        this.tickerFocusHold = false;
        this.releaseTicker();
      };
      this.onTickerVisibility = () => this.syncTickerLifecycle();
      this.onTickerMotionPreference = () => {
        this.motion = !reduceMotion.matches;
        if (this.motion) {
          document.documentElement.classList.add('ae-motion');
          this.measureTicker(true);
        }
        else this.stopTicker(true);
      };
      this.onTickerResize = () => {
        clearTimeout(this.tickerResizeTimer);
        this.tickerResizeTimer = setTimeout(() => this.measureTicker(true), 180);
      };
      this.ticker?.addEventListener('pointerenter', this.onTickerEnter, { passive: true });
      this.ticker?.addEventListener('pointerleave', this.onTickerLeave, { passive: true });
      this.ticker?.addEventListener('focusin', this.onTickerFocusIn);
      this.ticker?.addEventListener('focusout', this.onTickerFocusOut);
      document.addEventListener('visibilitychange', this.onTickerVisibility);
      reduceMotion.addEventListener?.('change', this.onTickerMotionPreference);
      addEventListener('resize', this.onTickerResize, { passive: true });
      if ('IntersectionObserver' in window && this.ticker) {
        this.tickerObserver = new IntersectionObserver(([entry]) => {
          this.tickerInView = entry.isIntersecting;
          this.syncTickerLifecycle();
        }, { threshold: 0 });
        this.tickerObserver.observe(this.ticker);
      }
      requestAnimationFrame(() => this.measureTicker(true));
    }
    disconnectedCallback() {
      this.removeEventListener('pointermove', this.onMove);
      this.removeEventListener('pointerleave', this.onLeave);
      if (this.pointerFrame) cancelAnimationFrame(this.pointerFrame);
      this.ticker?.removeEventListener('pointerenter', this.onTickerEnter);
      this.ticker?.removeEventListener('pointerleave', this.onTickerLeave);
      this.ticker?.removeEventListener('focusin', this.onTickerFocusIn);
      this.ticker?.removeEventListener('focusout', this.onTickerFocusOut);
      document.removeEventListener('visibilitychange', this.onTickerVisibility);
      reduceMotion.removeEventListener?.('change', this.onTickerMotionPreference);
      removeEventListener('resize', this.onTickerResize);
      clearTimeout(this.tickerResizeTimer);
      this.tickerObserver?.disconnect();
      this.stopTicker(false);
    }
    get tickerInteractionHold() {
      return this.tickerHoverHold || this.tickerFocusHold;
    }
    get tickerLifecyclePaused() {
      return !this.tickerInView || document.hidden;
    }
    measureTicker(restart = false) {
      if (!this.motion || !this.ticker || !this.tickerTrack || !this.tickerGroup || !this.tickerGroup.children.length || typeof this.tickerTrack.animate !== 'function') {
        this.stopTicker(true);
        return;
      }
      const first = this.tickerGroup.children[0].getBoundingClientRect();
      const second = this.tickerGroup.children[1]?.getBoundingClientRect();
      const styles = getComputedStyle(this.tickerGroup);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      this.tickerPitch = second ? Math.abs(second.left - first.left) : first.width + gap;
      this.tickerGroupWidth = this.tickerGroup.getBoundingClientRect().width;
      this.tickerVisibleCount = Math.max(1, Math.floor((this.ticker.clientWidth + gap + .5) / this.tickerPitch));
      this.tickerStep = Math.min(this.tickerVisibleCount, this.tickerGroup.children.length) * this.tickerPitch;
      this.tickerOffset = this.tickerGroupWidth ? this.tickerOffset % this.tickerGroupWidth : 0;
      this.ticker.classList.add('is-train-ready');
      if (restart) {
        this.tickerAnimation?.cancel();
        this.tickerAnimation = null;
        clearTimeout(this.tickerDwellTimer);
        this.tickerTrack.style.transform = `translate3d(${-this.tickerOffset}px, 0, 0)`;
        this.startTickerDwell(true);
      }
    }
    startTickerMove() {
      if (!this.motion || this.tickerLifecyclePaused || this.tickerInteractionHold || !this.tickerStep || !this.tickerGroupWidth) return;
      clearTimeout(this.tickerDwellTimer);
      this.tickerPhase = 'moving';
      this.ticker?.classList.remove('is-dwelling');
      this.tickerItems.forEach((item) => item.classList.remove('is-framed'));
      const start = this.tickerOffset;
      const target = start + this.tickerStep;
      this.tickerAnimation = this.tickerTrack.animate(
        [{ transform: `translate3d(${-start}px, 0, 0)` }, { transform: `translate3d(${-target}px, 0, 0)` }],
        { duration: this.tickerTravel, easing: 'cubic-bezier(.65, 0, .35, 1)', fill: 'forwards' }
      );
      this.tickerAnimation.onfinish = () => {
        this.tickerOffset = target >= this.tickerGroupWidth ? target - this.tickerGroupWidth : target;
        this.tickerAnimation.cancel();
        this.tickerAnimation = null;
        this.tickerTrack.style.transform = `translate3d(${-this.tickerOffset}px, 0, 0)`;
        this.startTickerDwell(true);
      };
    }
    startTickerDwell(reset = false) {
      clearTimeout(this.tickerDwellTimer);
      this.tickerPhase = 'dwelling';
      if (reset) this.tickerDwellRemaining = this.tickerDwell;
      this.ticker?.classList.add('is-dwelling');
      this.updateFramedTickerItems();
      if (this.tickerLifecyclePaused || this.tickerInteractionHold || !this.motion) return;
      this.tickerDwellStartedAt = performance.now();
      this.tickerDwellTimer = setTimeout(() => this.startTickerMove(), this.tickerDwellRemaining);
    }
    pauseTickerDwell() {
      if (!this.tickerDwellTimer) return;
      this.tickerDwellRemaining = Math.max(0, this.tickerDwellRemaining - (performance.now() - this.tickerDwellStartedAt));
      clearTimeout(this.tickerDwellTimer);
      this.tickerDwellTimer = 0;
    }
    resumeTickerDwell() {
      if (this.tickerPhase !== 'dwelling' || this.tickerLifecyclePaused || this.tickerInteractionHold || !this.motion) return;
      if (this.tickerDwellRemaining <= 0) this.startTickerMove();
      else this.startTickerDwell(false);
    }
    holdTicker() {
      if (this.tickerPhase === 'dwelling') this.pauseTickerDwell();
    }
    releaseTicker() {
      if (!this.tickerInteractionHold) this.resumeTickerDwell();
    }
    syncTickerLifecycle() {
      if (this.tickerLifecyclePaused) {
        if (this.tickerPhase === 'moving') this.tickerAnimation?.pause();
        else this.pauseTickerDwell();
      } else if (this.tickerPhase === 'moving') {
        this.tickerAnimation?.play();
      } else {
        this.resumeTickerDwell();
      }
    }
    updateFramedTickerItems() {
      const itemCount = this.tickerGroup?.children.length || 0;
      if (!itemCount || !this.tickerPitch) return;
      const firstIndex = Math.round(this.tickerOffset / this.tickerPitch) % itemCount;
      const framed = new Set(Array.from({ length: this.tickerVisibleCount }, (_, index) => (firstIndex + index) % itemCount));
      this.tickerItems.forEach((item, index) => item.classList.toggle('is-framed', framed.has(index % itemCount)));
    }
    stopTicker(reset) {
      clearTimeout(this.tickerDwellTimer);
      this.tickerDwellTimer = 0;
      this.tickerAnimation?.cancel();
      this.tickerAnimation = null;
      this.tickerPhase = 'idle';
      this.ticker?.classList.remove('is-dwelling', 'is-train-ready');
      this.tickerItems?.forEach((item) => item.classList.remove('is-framed'));
      if (reset && this.tickerTrack) {
        this.tickerOffset = 0;
        this.tickerTrack.style.transform = '';
      }
    }
  }

  class AeScentCalendar extends HTMLElement {
    connectedCallback() {
      if (this.aeReady) return;
      this.aeReady = true;
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.panel = this.querySelector('.ae-calendar__panel');
      this.gradientA = this.querySelector('.ae-calendar__gradient--a');
      this.gradientB = this.querySelector('.ae-calendar__gradient--b');
      this.activeGradient = 'a';
      this.autoplayDelay = 6000;
      this.autoplayRemaining = this.autoplayDelay;
      this.autoplayTimer = 0;
      this.autoplayStartedAt = 0;
      this.autoplayStopped = reduceMotion.matches;
      this.inView = true;
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index, true));
        tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
      });
      this.querySelectorAll('[data-calendar-direction]').forEach((button) => {
        button.addEventListener('click', () => {
          const direction = Number(button.dataset.calendarDirection);
          const next = (this.activeIndex + direction + this.tabs.length) % this.tabs.length;
          this.select(next, true);
          this.tabs[next]?.focus();
        });
      });
      // Autoplay rules: every deliberate action on the section — choosing a month (pill, arrow, arrow keys), a click/tap,
      // mouse movement over products, sideways scroll of the bottle or month rails, focus, keys — holds the current month
      // and autoplay resumes from where the bar left off after `idleResume` ms without interaction. Only reduced-motion stops it.
      // Ordinary page scrolling past the section (wheel / vertical touch drag) is NOT an interaction.
      this.idleResume = Number(this.dataset.aeAutoplayIdle) || 8000;
      this.idleTimer = 0;
      this.onInteract = () => {
        if (this.autoplayStopped) return;
        this.pauseAutoplay();
        clearTimeout(this.idleTimer);
        this.idleTimer = setTimeout(() => { this.idleTimer = 0; this.startAutoplay(false); }, this.idleResume);
      };
      this.onPointerMove = (event) => {
        if (event.pointerType !== 'mouse') return;
        // Browsers re-fire a mouse-move after page scroll without the mouse moving; only count real movement over products/controls.
        const moved = this.lastPointer && (Math.abs(event.clientX - this.lastPointer.x) + Math.abs(event.clientY - this.lastPointer.y)) > 3;
        this.lastPointer = { x: event.clientX, y: event.clientY };
        if (moved && event.target.closest?.('a, button, .ae-product-card, .ae-calendar__tab')) this.onInteract();
      };
      // Rail scrolls we trigger ourselves (centring the active pill) are not customer interactions.
      this.onRailScroll = () => { if (Date.now() > (this.ownScrollUntil || 0)) this.onInteract(); };
      this.addEventListener('click', this.onInteract);
      this.addEventListener('pointermove', this.onPointerMove, { passive: true });
      this.addEventListener('focusin', this.onInteract);
      this.addEventListener('keydown', this.onInteract);
      this.rails = [...this.querySelectorAll('.ae-calendar__products, .ae-calendar__tabs')];
      this.rails.forEach((rail) => rail.addEventListener('scroll', this.onRailScroll, { passive: true }));
      const today = new Date();
      const dateKey = `${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const match = this.tabs.findIndex((tab) => dateKey >= tab.dataset.start && dateKey <= tab.dataset.end);
      if (match >= 0) {
        this.tabs[match].classList.add('is-now');
        const marker = this.tabs[match].querySelector('.ae-calendar__now');
        if (marker) marker.hidden = false;
      }
      this.select(match >= 0 ? match : 0, false, true);
      this.onVisibility = () => this.syncAutoplay();
      document.addEventListener('visibilitychange', this.onVisibility);
      this.onMotionPreference = () => { if (reduceMotion.matches) this.stopAutoplay(); };
      reduceMotion.addEventListener?.('change', this.onMotionPreference);
      if (!this.autoplayStopped && 'IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          this.inView = entry.isIntersecting;
          this.syncAutoplay();
        }, { threshold: .15 });
        this.visibilityObserver.observe(this);
      }
      this.syncAutoplay(true);
    }
    disconnectedCallback() {
      clearTimeout(this.autoplayTimer);
      clearTimeout(this.idleTimer);
      document.removeEventListener('visibilitychange', this.onVisibility);
      reduceMotion.removeEventListener?.('change', this.onMotionPreference);
      this.removeEventListener('click', this.onInteract);
      this.removeEventListener('pointermove', this.onPointerMove);
      this.removeEventListener('focusin', this.onInteract);
      this.removeEventListener('keydown', this.onInteract);
      this.rails?.forEach((rail) => rail.removeEventListener('scroll', this.onRailScroll));
      this.visibilityObserver?.disconnect();
    }
    onKeydown(event, index) {
      let next = index;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % this.tabs.length;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + this.tabs.length) % this.tabs.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.tabs.length - 1;
      else return;
      event.preventDefault();
      this.select(next, true);
      this.tabs[next].focus();
    }
    select(index, scroll, initial = false) {
      if (!this.tabs[index]) return;
      this.activeIndex = index;
      this.style.setProperty('--ae-active', index);
      this.tabs.forEach((tab, i) => {
        tab.setAttribute('aria-selected', String(i === index));
        tab.tabIndex = i === index ? 0 : -1;
      });
      this.panels.forEach((panel, i) => {
        panel.classList.toggle('is-active', i === index);
        panel.hidden = i !== index;
      });
      const panel = this.panels[index];
      const gradientA = panel.dataset.gradientA || '#f3c98b';
      const gradientB = panel.dataset.gradientB || '#8a3b1e';
      this.style.setProperty('--ae-grad-a', gradientA);
      this.style.setProperty('--ae-grad-b', gradientB);
      this.crossfadeGradient(gradientA, gradientB, initial);
      if (scroll && window.innerWidth < 750) {
        this.ownScrollUntil = Date.now() + 1200;
        this.tabs[index].scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
      }
      if (!initial && !this.autoplayStopped) this.startAutoplay(true);
    }
    crossfadeGradient(colorA, colorB, initial) {
      if (!this.panel || !this.gradientA || !this.gradientB) return;
      if (initial) {
        this.gradientA.style.setProperty('--ae-layer-a', colorA);
        this.gradientA.style.setProperty('--ae-layer-b', colorB);
        this.gradientB.style.setProperty('--ae-layer-a', colorA);
        this.gradientB.style.setProperty('--ae-layer-b', colorB);
        this.panel.classList.remove('is-gradient-b');
        this.activeGradient = 'a';
        return;
      }
      const nextLayer = this.activeGradient === 'a' ? this.gradientB : this.gradientA;
      nextLayer.style.setProperty('--ae-layer-a', colorA);
      nextLayer.style.setProperty('--ae-layer-b', colorB);
      this.activeGradient = this.activeGradient === 'a' ? 'b' : 'a';
      requestAnimationFrame(() => this.panel.classList.toggle('is-gradient-b', this.activeGradient === 'b'));
    }
    startAutoplay(reset = false) {
      if (this.autoplayStopped || reduceMotion.matches || !this.inView || document.hidden || this.tabs.length < 2) return;
      clearTimeout(this.autoplayTimer);
      if (reset) this.autoplayRemaining = this.autoplayDelay;
      this.classList.remove('is-autoplay-paused');
      this.autoplayStartedAt = Date.now();
      this.autoplayTimer = setTimeout(() => {
        const next = (this.activeIndex + 1) % this.tabs.length;
        this.select(next, true);
      }, this.autoplayRemaining);
    }
    pauseAutoplay() {
      if (this.autoplayStopped) return;
      if (this.autoplayTimer) {
        this.autoplayRemaining = Math.max(100, this.autoplayRemaining - (Date.now() - this.autoplayStartedAt));
        clearTimeout(this.autoplayTimer);
        this.autoplayTimer = 0;
      }
      this.classList.add('is-autoplay-paused');
    }
    stopAutoplay() {
      if (this.autoplayStopped) return;
      this.autoplayStopped = true;
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = 0;
      this.classList.remove('is-autoplay-paused');
      this.classList.add('is-autoplay-stopped');
    }
    syncAutoplay(initial = false) {
      if (this.autoplayStopped) {
        this.classList.add('is-autoplay-stopped');
        return;
      }
      if (this.inView && !document.hidden && !this.idleTimer) this.startAutoplay(initial);
      else this.pauseAutoplay();
    }
  }

  class AeScrollBand extends HTMLElement {
    connectedCallback() {
      if (reduceMotion.matches || !('IntersectionObserver' in window)) return;
      const nativeTimeline = typeof CSS !== 'undefined' && CSS.supports?.('animation-timeline', 'view()');
      if (nativeTimeline) return;
      this.frame = 0;
      this.progress = -1;
      this.measure = this.measure.bind(this);
      this.queue = () => { if (!this.frame) this.frame = requestAnimationFrame(this.measure); };
      this.observer = new IntersectionObserver(([entry]) => entry.isIntersecting ? this.listen() : this.stop(), { rootMargin: '100% 0px' });
      this.observer.observe(this);
    }
    disconnectedCallback() { this.stop(); this.observer?.disconnect(); }
    listen() {
      if (this.listening) return;
      this.listening = true;
      addEventListener('scroll', this.queue, { passive: true });
      addEventListener('resize', this.queue, { passive: true });
      this.measure();
    }
    stop() {
      this.listening = false;
      removeEventListener('scroll', this.queue);
      removeEventListener('resize', this.queue);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
    }
    measure() {
      this.frame = 0;
      const rect = this.getBoundingClientRect();
      const viewport = innerHeight || document.documentElement.clientHeight;
      const value = Math.round(Math.min(1, Math.max(0, (viewport - rect.top) / (viewport + rect.height))) * 1000) / 1000;
      if (value !== this.progress) {
        this.progress = value;
        this.style.setProperty('--ae-band-progress', value);
      }
    }
  }

  class AeScentFinder extends HTMLElement {
    connectedCallback() {
      this.steps = [...this.querySelectorAll('.ae-finder__step')];
      this.result = this.querySelector('.ae-finder__result');
      this.live = this.querySelector('.ae-finder__live');
      this.progress = this.querySelector('[data-finder-progress]');
      this.answers = {};
      this.classList.add('is-enhanced');
      this.steps.forEach((step, index) => { step.hidden = index !== 0; });
      this.result.hidden = true;
      this.addEventListener('change', (event) => {
        const input = event.target.closest('input[data-answer]');
        if (!input) return;
        this.answers[input.name] = input.value;
        if (input.name === 'mood') this.showStep(1);
        else this.showResult();
      });
      this.addEventListener('click', (event) => {
        const action = event.target.closest('[data-finder-action]')?.dataset.finderAction;
        if (action === 'back') this.showStep(0);
        if (action === 'restart') this.restart();
      });
    }
    showStep(index) {
      this.steps.forEach((step, i) => { step.hidden = i !== index; });
      this.result.hidden = true;
      this.result.classList.remove('is-arriving');
      if (this.progress) this.progress.textContent = `${index + 1} / 2`;
      const legend = this.steps[index]?.querySelector('.ae-finder__legend');
      legend?.setAttribute('tabindex', '-1');
      legend?.focus({ preventScroll: true });
      this.live.textContent = `Question ${index + 1} of 2`;
    }
    showResult() {
      const key = `${this.answers.mood}-${this.answers.use}`;
      const source = this.querySelector(`[data-result-key="${key}"]`);
      if (!source) return;
      this.steps.forEach((step) => { step.hidden = true; });
      this.result.querySelector('[data-result-title]').textContent = source.dataset.title;
      this.result.querySelector('[data-result-text]').textContent = source.dataset.text;
      this.result.querySelector('[data-result-products]').innerHTML = source.innerHTML;
      this.result.hidden = false;
      if (this.progress) this.progress.textContent = 'Your match';
      this.result.classList.remove('is-arriving');
      void this.result.offsetWidth;
      this.result.classList.add('is-arriving');
      const title = this.result.querySelector('[data-result-title]');
      title.setAttribute('tabindex', '-1');
      title.focus({ preventScroll: true });
      this.live.textContent = `Your result: ${source.dataset.title}`;
      reveal(this.result);
      enhanceQuickAdd(this.result);
    }
    restart() {
      this.answers = {};
      this.querySelectorAll('input[data-answer]').forEach((input) => { input.checked = false; });
      this.showStep(0);
      this.live.textContent = 'The scent finder has been reset.';
    }
  }

  const enhanceQuickAdd = (root = document) => {
    root.querySelectorAll('[data-ae-quick-add]:not([data-ae-ready])').forEach((form) => {
      form.dataset.aeReady = 'true';
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (form.classList.contains('is-loading')) return;
        const button = form.querySelector('button');
        const label = form.querySelector('[data-ae-add-label]');
        let live = form.querySelector('.ae-live');
        if (!live) {
          live = document.createElement('span');
          live.className = 'ae-live';
          live.setAttribute('aria-live', 'polite');
          form.append(live);
        }
        form.classList.add('is-loading');
        button.disabled = true;
        try {
          const body = new FormData(form);
          const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          const sectionIds = cart?.getSectionsToRender ? cart.getSectionsToRender().map((s) => s.id) : ['cart-drawer', 'cart-icon-bubble'];
          body.append('sections', sectionIds.join(','));
          body.append('sections_url', window.location.pathname);
          const addUrl = window.routes && window.routes.cart_add_url ? `${window.routes.cart_add_url}.js` : '/cart/add.js';
          const response = await fetch(addUrl, { method: 'POST', body, headers: { Accept: 'application/json' } });
          if (!response.ok) {
            const error = new Error('Cart add failed');
            error.status = response.status;
            try { error.detail = (await response.json()).description || ''; } catch (_) { error.detail = ''; }
            throw error;
          }
          const item = await response.json();
          let sections = item.sections;
          if (!sections) {
            const sectionsResponse = await fetch(`/cart?sections=${sectionIds.join(',')}`);
            if (sectionsResponse.ok) sections = await sectionsResponse.json();
          }
          if (cart?.renderContents && sections) {
            cart.setActiveElement?.(button);
            cart.renderContents({ ...item, id: item.id, key: item.key, sections });
          }
          else if (sections?.['cart-icon-bubble']) {
            const bubble = document.querySelector('#cart-icon-bubble');
            const replacement = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('#cart-icon-bubble');
            if (bubble && replacement) bubble.replaceWith(replacement);
          }
          form.classList.add('is-added');
          label.textContent = 'Added';
          live.textContent = `${form.dataset.productTitle || 'Product'} added to your basket`;
          setTimeout(() => { form.classList.remove('is-added'); label.textContent = '+'; }, 1800);
        } catch (error) {
          label.textContent = error.status === 422 ? 'Unavailable' : 'Try again';
          live.textContent = error.detail || `We couldn't add ${form.dataset.productTitle || 'that product'}. Try again.`;
          setTimeout(() => { label.textContent = '+'; }, 2500);
        } finally {
          form.classList.remove('is-loading');
          button.disabled = false;
        }
      });
    });
  };

  const enhanceEditTilt = (root = document) => {
    if (reduceMotion.matches || !finePointer.matches) return;
    const cards = [...root.querySelectorAll('.ae-edit__rail .ae-product-card:not([data-ae-tilt-ready])')];
    cards.forEach((card) => {
      card.dataset.aeTiltReady = 'true';
      let frame = 0;
      let pointer = null;
      const renderTilt = () => {
        frame = 0;
        if (!pointer) return;
        const rect = card.getBoundingClientRect();
        const x = Math.max(-1, Math.min(1, ((pointer.x - rect.left) / rect.width - .5) * 2));
        const y = Math.max(-1, Math.min(1, ((pointer.y - rect.top) / rect.height - .5) * 2));
        card.style.willChange = 'transform';
        card.style.transform = `rotateX(${-y * 6}deg) rotateY(${x * 6}deg)`;
      };
      card.addEventListener('pointermove', (event) => {
        pointer = { x: event.clientX, y: event.clientY };
        if (!frame) frame = requestAnimationFrame(renderTilt);
      }, { passive: true });
      card.addEventListener('pointerleave', () => {
        pointer = null;
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
        card.style.transform = '';
        card.style.willChange = '';
      });
    });
  };

  const reelControllers = new WeakMap();

  const reelSections = (root) => {
    const sections = [];
    if (root.matches?.('[data-ae-reels]')) sections.push(root);
    sections.push(...root.querySelectorAll?.('[data-ae-reels]') || []);
    return sections;
  };

  const enhanceProductReels = (root = document) => {
    if (reduceMotion.matches || !('IntersectionObserver' in window)) return;
    reelSections(root).forEach((section) => {
      if (reelControllers.has(section)) return;
      const reels = [...section.querySelectorAll('[data-ae-reel]')].filter((reel) => reel.querySelector('.ae-reel__track')?.children.length > 1);
      if (!reels.length) return;

      const state = {
        animations: new Set(),
        duration: Number(section.dataset.aeReelDuration) || 2800,
        hasSpun: false,
        inView: false,
        introEnabled: section.dataset.aeReelIntroEnabled !== 'false',
        introHold: Number(section.dataset.aeReelIntroHold) || 650,
        leftAt: 0,
        pendingVisibility: false,
        running: false,
        stagger: Number(section.dataset.aeReelStagger) || 700,
        timers: new Set()
      };

      const resetReel = (reel) => {
        reel.classList.remove('is-intro-active', 'is-intro-exiting', 'is-ready', 'is-spinning');
        reel.closest('.ae-product-card')?.classList.remove('is-reel-settling');
        reel.querySelectorAll('[data-ae-reel-glyph]').forEach((glyph) => {
          glyph.textContent = glyph.dataset.aeReelGlyph;
        });
        const track = reel.querySelector('.ae-reel__track');
        if (track) track.style.transform = '';
      };

      const setTimer = (callback, delay) => {
        const timer = setTimeout(() => {
          state.timers.delete(timer);
          callback();
        }, delay);
        state.timers.add(timer);
        return timer;
      };

      const cancel = () => {
        state.timers.forEach((timer) => clearTimeout(timer));
        state.timers.clear();
        state.animations.forEach((animation) => animation.cancel());
        state.animations.clear();
        reels.forEach(resetReel);
        state.running = false;
      };

      const canRun = () => !document.hidden && !reduceMotion.matches && state.inView;

      const startTrack = (reel, complete) => {
        if (!canRun()) {
          resetReel(reel);
          complete();
          return;
        }

        const track = reel.querySelector('.ae-reel__track');
        const distance = reel.clientWidth * (track.children.length - 1);
        if (!distance || typeof track.animate !== 'function') {
          resetReel(reel);
          complete();
          return;
        }

        track.style.transform = 'translate3d(0, 0, 0)';
        reel.classList.add('is-ready', 'is-spinning');
        const travel = track.animate(
          [
            { transform: 'translate3d(0, 0, 0)' },
            { transform: `translate3d(${-distance}px, 0, 0)` }
          ],
          { duration: state.duration, easing: 'cubic-bezier(.3,.7,.2,1)', fill: 'forwards' }
        );
        state.animations.add(travel);
        travel.onfinish = () => {
          state.animations.delete(travel);
          track.style.transform = `translate3d(${-distance}px, 0, 0)`;
          travel.cancel();
          reel.classList.remove('is-spinning');
          const card = reel.closest('.ae-product-card');
          card?.classList.add('is-reel-settling');
          const settle = track.animate(
            [
              { transform: `translate3d(${-distance + 4}px, 0, 0)` },
              { transform: `translate3d(${-distance}px, 0, 0)` }
            ],
            { duration: 360, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards' }
          );
          state.animations.add(settle);
          settle.onfinish = () => {
            state.animations.delete(settle);
            settle.cancel();
            resetReel(reel);
            complete();
          };
        };
      };

      const playCaption = (reel, complete) => {
        const intro = reel.querySelector('.ae-reel__intro');
        const glyphs = [...reel.querySelectorAll('[data-ae-reel-glyph]')];
        if (!state.introEnabled || !intro || !glyphs.length || !canRun()) {
          complete();
          return;
        }
        const revealDuration = (glyphs.length - 1) * 90 + 260;
        reel.classList.remove('is-intro-exiting');
        reel.classList.add('is-intro-active');
        setTimer(() => {
          if (!canRun()) {
            resetReel(reel);
            complete();
            return;
          }
          reel.classList.add('is-intro-exiting');
          setTimer(() => {
            reel.classList.remove('is-intro-active', 'is-intro-exiting');
            complete();
          }, 320);
        }, revealDuration + state.introHold);
      };

      const spin = () => {
        if (state.running || document.hidden || reduceMotion.matches || !state.inView) return;
        state.running = true;
        state.hasSpun = true;
        state.leftAt = 0;
        let remaining = reels.length;
        const complete = () => {
          remaining -= 1;
          if (!remaining) state.running = false;
        };

        reels.forEach((reel, index) => {
          setTimer(() => {
            if (!canRun()) {
              resetReel(reel);
              complete();
              return;
            }
            startTrack(reel, () => playCaption(reel, complete));
          }, index * state.stagger);
        });
      };

      const eligible = () => !state.hasSpun || (state.leftAt && Date.now() - state.leftAt >= 12000);
      const trySpin = () => {
        if (!state.inView || !eligible()) return;
        if (document.hidden) {
          state.pendingVisibility = true;
          return;
        }
        state.pendingVisibility = false;
        spin();
      };

      state.observer = new IntersectionObserver(([entry]) => {
        state.inView = entry.isIntersecting;
        if (!entry.isIntersecting) {
          if (state.hasSpun) state.leftAt = Date.now();
          state.pendingVisibility = false;
          cancel();
          return;
        }
        if (entry.intersectionRatio >= .35) trySpin();
      }, { threshold: [0, .35] });

      state.onVisibility = () => {
        if (document.hidden) {
          if (state.running) state.pendingVisibility = true;
          cancel();
        }
        else if (state.pendingVisibility) {
          state.pendingVisibility = false;
          spin();
        }
      };
      state.onMotionPreference = () => {
        if (reduceMotion.matches) {
          state.observer.disconnect();
          state.pendingVisibility = false;
          cancel();
        }
        else state.observer.observe(section);
      };
      state.destroy = () => {
        cancel();
        state.observer.disconnect();
        document.removeEventListener('visibilitychange', state.onVisibility);
        reduceMotion.removeEventListener?.('change', state.onMotionPreference);
        reelControllers.delete(section);
      };

      reelControllers.set(section, state);
      document.addEventListener('visibilitychange', state.onVisibility);
      reduceMotion.addEventListener?.('change', state.onMotionPreference);
      state.observer.observe(section);
    });
  };

  const destroyProductReels = (root) => {
    reelSections(root).forEach((section) => reelControllers.get(section)?.destroy());
  };

  const pauseAmbientMotion = (root = document) => {
    const sections = [];
    if (root.matches?.('.ae-section:not([data-ae-pause-ready])')) sections.push(root);
    sections.push(...root.querySelectorAll('.ae-section:not([data-ae-pause-ready])'));
    if (!sections.length || reduceMotion.matches || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        entry.target.dataset.aeInView = String(entry.isIntersecting);
        entry.target.classList.toggle('ae-motion-paused', !entry.isIntersecting || document.hidden);
      });
    }, { threshold: 0 });
    sections.forEach((section) => {
      section.dataset.aePauseReady = 'true';
      observer.observe(section);
    });
  };

  if (!customElements.get('ae-autumn-hero')) customElements.define('ae-autumn-hero', AeHero);
  if (!customElements.get('ae-scent-calendar')) customElements.define('ae-scent-calendar', AeScentCalendar);
  if (!customElements.get('ae-scroll-band')) customElements.define('ae-scroll-band', AeScrollBand);
  if (!customElements.get('ae-scent-finder')) customElements.define('ae-scent-finder', AeScentFinder);

  const init = (root = document) => {
    if (!reduceMotion.matches) document.documentElement.classList.add('ae-motion');
    reveal(root);
    enhanceQuickAdd(root);
    enhanceEditTilt(root);
    enhanceProductReels(root);
    pauseAmbientMotion(root);
  };
  init();
  document.addEventListener('visibilitychange', () => {
    document.querySelectorAll('.ae-section[data-ae-pause-ready]').forEach((section) => {
      section.classList.toggle('ae-motion-paused', document.hidden || section.dataset.aeInView === 'false');
    });
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyProductReels(event.target));
  reduceMotion.addEventListener?.('change', () => { if (!reduceMotion.matches) enhanceProductReels(); });
})();
